import { ComponentType, ReactNode, SyntheticEvent, ChangeEvent, useEffect, useState } from "react"

import { ContentObjectItem } from "@vertesia/common"
import { JSONSchema4Object } from "json-schema"
import { Plus, X } from "lucide-react"

import { SearchDocumentsButton } from "@components/buttons/SearchDocumentsButton"
import { UploadDocumentButton } from "@components/buttons/UploadDocumentButton"
import { UploadStatus } from "@components/form/UploadStatus"

import { FormHelper, FormLabel } from "@plugins/form/Fields"
import { FormContextProvider, InputComponentProps, useForm } from "@plugins/form/FormContext"
import { ManagedListProperty, ManagedObject, ManagedObjectBase, ManagedProperty, Node } from "@plugins/form/ManagedObject"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"
import { Input } from "@ui/input"
import { Label } from "@ui/label"
import { Switch } from "@ui/switch"
import { Textarea } from "@ui/textarea"

interface FormProps {
    object: ManagedObject
    components?: Record<string, ComponentType<InputComponentProps>>
    children?: ReactNode | ReactNode[]
    onSubmit?: (data: JSONSchema4Object) => void
    onChange?: (prop: Node) => void
}
export function Form({ object, components, onSubmit, children, onChange }: Readonly<FormProps>) {
    const _onSubmit = (evt: SyntheticEvent) => {
        evt.stopPropagation()
        evt.preventDefault()
        onSubmit && onSubmit(object.value)
    }
    object.observer = onChange
    return (
        <FormContextProvider value={{ object, components: components || {} }}>
            <form className="w-full" onSubmit={_onSubmit}>
                {children}
            </form>
        </FormContextProvider>
    )
}

function GeneratedFormFields() {
    const ctx = useForm()
    return <div className="flex flex-col gap-6">{ctx.object.properties.map(renderProperty)}</div>
}

export function GeneratedForm({ children, ...props }: Readonly<FormProps>) {
    return (
        <Form {...props}>
            <GeneratedFormFields />
            {children}
        </Form>
    )
}

function renderProperty(prop: Node) {
    if (prop.isList) {
        return <ListField key={prop.name} object={prop as ManagedListProperty} />
    } else if (prop.isObject) {
        return <CompositeField key={prop.name} object={prop as ManagedObjectBase} />
    } else {
        return <ScalarField key={prop.name} object={prop as ManagedProperty} />
    }
}

function renderItemProperty(prop: Node, editor?: string) {
    if (prop.isList) {
        return <ListField key={prop.name} object={prop as ManagedListProperty} />
    } else if (prop.isObject) {
        return <CompositeField object={prop as ManagedObjectBase} />
    } else {
        return <ScalarField object={prop as ManagedProperty} editor={editor} />
    }
}

interface ScalarFieldProps {
    object: ManagedProperty
    editor?: string
}
export function ScalarField({ object, editor }: Readonly<ScalarFieldProps>) {
    if (!editor) {
        editor = object.schema.editor
    }

    const inputType = object.getInputType()

    if (inputType === "checkbox") {
        return <CheckboxField object={object} />
    } else if (editor === "media" || editor === "document") {
        return <DocumentField object={object} />
    } else {
        return <TextField object={object} />
    }
}

interface TextFieldProps {
    object: ManagedProperty
}
function TextField({ object }: Readonly<TextFieldProps>) {
    const [value, setValue] = useState<string>(object.value as string)

    const inputType = object.getInputType()
    const isTextArea = inputType === "textarea"

    const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target
        setValue(value)
        object.value = object.schema.isNumber ? parseFloat(value) : value
    }

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-col gap-0.5 w-full">
                <Label htmlFor={object.name}>{!object.isListItem ? <FormLabel required={object.schema.isRequired}>{object.title}</FormLabel> : ""}</Label>
                {isTextArea ? <Textarea id={object.name} value={value} onChange={handleOnChange} placeholder="Enter text here..." className="w-full h-24 p-2 border border-gray-300 rounded-md h-8" /> : <Input id={object.name} value={value} onChange={handleOnChange} type={inputType} placeholder="Enter value here..." />}
            </div>
            {object.schema.description && <FormHelper>{object.schema.description}</FormHelper>}
        </div>
    )
}

interface CheckboxFieldProps {
    object: ManagedProperty
}
function CheckboxField({ object }: Readonly<CheckboxFieldProps>) {
    const [checked, setChecked] = useState<boolean>(object.value as boolean)

    const handleOnChange = (value: boolean) => {
        setChecked(value)
        object.value = value
    }

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-row items-center">
                <Switch id={object.name} checked={checked} onCheckedChange={handleOnChange} className={`${!object.isListItem ? "mr-2" : ""}`} />
                {!object.isListItem && <FormLabel required={object.schema.isRequired}>{object.title}</FormLabel>}
            </div>
            {object.schema.description && <FormHelper>{object.schema.description}</FormHelper>}
        </div>
    )
}

function getDocumentValue(object: ManagedProperty): string {
    if (!object.value || typeof object.value !== "string") {
        return ""
    }

    let value = object.value as string
    if (value?.startsWith("store:")) {
        value = value.substring(6)
    }
    return value
}

interface DocumentFieldProps {
    object: ManagedProperty
}
export function DocumentField({ object }: Readonly<DocumentFieldProps>) {
    const { client } = useUserSession()

    const [name, setName] = useState<string | undefined>(undefined)
    const [uploadedDocumentId, setUploadedDocumentId] = useState<string | undefined>(getDocumentValue(object))

    const handleOnDocumentChange = (value: ContentObjectItem) => {
        setUploadedDocumentId(value.id)
        object.value = value ? `store:${value.id.toString()}` : ""
        setName(value.name)
    }

    const loadDocumentName = () => {
        if (!object || !object.value || !client || typeof object.value !== "string") {
            setName(undefined)
            return
        }

        let value = object.value as string

        if (value?.startsWith("store:")) {
            value = value.substring(6)
            client.objects
                .retrieve(value)
                .then((doc) => {
                    setName(doc.properties.title || doc.name || `store:${value}`)
                })
                .catch(() => {
                    setName(`store:${value}`)
                })
        } else {
            setName(value)
        }
    }

    useEffect(() => {
        loadDocumentName()
    }, [object.value, client])

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-row items-center">
                <div className="flex flex-col w-full gap-1">
                    <div className="flex flex-col min-w-0 flex-1">
                        <Label htmlFor={object.name} className="truncate">
                            <span className="truncate">
                                {object.title}
                                {object.schema.isRequired && <sup style={{ color: "red " }}>*</sup>}
                            </span>
                        </Label>
                    </div>
                    <div className="flex flex-row gap-2 w-full">
                        {uploadedDocumentId && <UploadStatus documentId={uploadedDocumentId} />}
                        <Input value={name} placeholder="Select or upload a document..." readOnly />
                        <div className="flex flex-row gap-2 flex-shrink-0">
                            <SearchDocumentsButton onSelection={handleOnDocumentChange} />
                            <UploadDocumentButton onUploadDone={handleOnDocumentChange} />
                        </div>
                    </div>
                    {object.schema.description && <Label className="text-start font-light truncate text-xs text-muted-foreground">{object.schema.description}</Label>}
                </div>
            </div>
        </div>
    )
}

interface ObjectFormProps {
    object: ManagedObjectBase
}
function CompositeField({ object }: Readonly<ObjectFormProps>) {
    return (
        <div className="border border-gray-200 rounded-lg p-4">
            {!object.isListItem && (
                <div className="mb-4">
                    <span className="font-semibold text-start">{object.title}</span>
                </div>
            )}
            <div className="space-y-4">{object.properties.map(renderProperty)}</div>
        </div>
    )
}

interface ListFieldProps {
    object: ManagedListProperty
}
function ListField({ object }: Readonly<ListFieldProps>) {
    const propertyType = object.schema.arraySchema.editor
    if (propertyType === "document" || propertyType === "media") {
        return <DocumentListField object={object} />
    }

    const [value, setValue] = useState<any[]>(object.value || [])

    const addItem = () => {
        object.add()
        setValue([...object.value])
    }

    const deleteItem = (index: number) => {
        object.remove(index)
        setValue([...object.value])
    }

    return (
        <div>
            {!object.isListItem && (
                <div className={`flex flex-row items-center justify-between min-w-0 ${object.items?.length ? "mb-1" : ""}`}>
                    <span className="font-semibold text-start truncate flex-1 mr-4">{object.title}</span>
                    <Button variant="ghost" size="sm" onClick={addItem} className="flex items-center gap-1 flex-shrink-0">
                        <Plus />
                    </Button>
                </div>
            )}
            {object.items?.length >= 1 && (
                <div className="flex flex-col gap-1 w-full">
                    {object.items.map((item, index) => {
                        return <ListItem key={`${index}-${value[index] ?? ""}`} object={item} list={object} onDelete={() => deleteItem(index)} />
                    })}
                </div>
            )}
        </div>
    )
}

function DocumentListField({ object }: Readonly<ListFieldProps>) {
    const { client } = useUserSession()
    const [value, setValue] = useState<any[]>(object.value || [])
    const [names, setNames] = useState<string[]>([])

    const handleOnDocumentsChange = (documents: ContentObjectItem[]) => {
        if (documents.length === 0) {
            return
        }

        documents.forEach((doc) => {
            addDocument(doc)
        })
        setValue([...object.value])
        loadAllDocumentNames()
    }

    const addDocument = (document: ContentObjectItem): boolean => {
        if (object.items.some((item) => item.value === `store:${document.id}`)) {
            return false
        }

        object.add()
        object.items[object.items.length - 1].value = `store:${document.id}`
        return true
    }

    const deleteItem = (index: number) => {
        object.remove(index)
        setValue([...object.value])
        setNames((prevNames) => prevNames.filter((_, i) => i !== index))
    }

    const loadDocumentName = async (value: string): Promise<string> => {
        if (!value || !client) {
            return value
        }

        let documentId = value
        if (value?.startsWith("store:")) {
            documentId = value.substring(6)
        }

        try {
            const doc = await client.objects.retrieve(documentId)
            return doc.properties?.title || doc.name || `store:${documentId}`
        } catch (error) {
            console.error("Failed to load document name:", error)
            return `store:${documentId}`
        }
    }

    const loadAllDocumentNames = async () => {
        if (!client || !object.items?.length) {
            setNames([])
            return
        }

        const namePromises = object.items.map((item) => loadDocumentName(item.value as string))

        try {
            const resolvedNames = await Promise.all(namePromises)
            setNames(resolvedNames)
        } catch (error) {
            console.error("Failed to load document names:", error)
            setNames(object.items.map((item) => item.value as string))
        }
    }

    useEffect(() => {
        loadAllDocumentNames()
    }, [object.items?.length, client])

    useEffect(() => {
        object.value = value
    }, [value])

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center justify-between min-w-0">
                <div className="flex flex-col min-w-0 flex-1 mr-4">
                    <Label htmlFor={object.name} className="truncate">
                        <span className="truncate">
                            {object.title}
                            {object.schema.isRequired && <sup style={{ color: "red " }}>*</sup>}
                        </span>
                        <span className="text-xs text-muted-foreground">(select or upload)</span>
                    </Label>
                    {object.schema.arraySchema.description && <Label className="text-start font-light truncate text-xs text-muted-foreground">{object.schema.arraySchema.description}</Label>}
                </div>
                <div className="flex flex-row gap-2 flex-shrink-0">
                    <SearchDocumentsButton onMultipleSelection={handleOnDocumentsChange} />
                    <UploadDocumentButton onMultipleUpload={handleOnDocumentsChange} />
                </div>
            </div>
            {object.items?.length >= 1 && (
                <div className="flex flex-col gap-2 w-full">
                    {object.items.map((item, index) => {
                        const documentId = getDocumentValue({ value: item.value } as ManagedProperty)
                        return (
                            <div key={`${index}-${value[index] ?? ""}`} className="flex flex-row w-full gap-2 hover:bg-gray-50 hover:rounded-md min-w-0">
                                <div className="flex flex-1 gap-4 items-center justify-center min-w-0">
                                    {documentId && <UploadStatus documentId={documentId} />}
                                    <Input value={names[index] || "Loading..."} placeholder="Select or upload a document..." readOnly className="truncate" />
                                </div>
                                <Button variant="outline" onClick={() => deleteItem(index)}>
                                    <X />
                                </Button>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

interface ListItemProps {
    list: ManagedListProperty
    object: Node & { index: number }
    onDelete: () => void
}
function ListItem({ list, object, onDelete }: Readonly<ListItemProps>) {
    return (
        <div className="flex flex-row w-full gap-4 hover:bg-gray-50 hover:rounded-md items-center">
            <div className="flex flex-1 -mt-0.5">{renderItemProperty(object, list.schema.arraySchema.editor)}</div>
            <Button variant="outline" onClick={onDelete}>
                <X />
            </Button>
        </div>
    )
}
